import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ExternalLink, Search, MapPin, Phone } from "lucide-react";
import { BRANDING_TIERS } from "../data/brandingAgencies";

function BrandCard({ a }) {
  const hasLogo = Boolean(a.domain);
  return (
    <a
      href={a.website}
      target="_blank"
      rel="noopener noreferrer"
      className="group glass-card p-6 flex flex-col h-full hover:border-[#7DA3D9]/60 transition-all"
      title={`Visit ${a.name}`}
      data-testid={`brand-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-md bg-white p-2.5 border border-white/10 group-hover:border-[#7DA3D9] group-hover:scale-105 transition-all shadow-lg flex-shrink-0">
          {hasLogo ? (
            <img
              src={`https://logo.clearbit.com/${a.domain}`}
              alt={`${a.name} logo`}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${a.domain}&sz=128`;
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#0A1128] font-display text-xl bg-gradient-to-br from-[#B8D0EE] to-[#7DA3D9] rounded-sm">
              {a.name[0]}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg leading-tight group-hover:text-[#B8D0EE] transition-colors">
            {a.name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1 inline-flex items-center gap-1">
            Visit <ExternalLink className="w-3 h-3" />
          </div>
        </div>
      </div>
      {a.description && (
        <p className="text-xs text-slate-400 leading-relaxed mt-4 line-clamp-3">{a.description}</p>
      )}
      {(a.address || a.phone) && (
        <div className="mt-4 pt-4 border-t border-white/5 space-y-1.5 text-xs text-slate-400">
          {a.address && (
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3 h-3 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{a.address}</span>
            </div>
          )}
          {a.phone && (
            <a
              href={`tel:${a.phone.replace(/[^+0-9]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 hover:text-[#B8D0EE] transition-colors notranslate"
            >
              <Phone className="w-3 h-3 text-[#7DA3D9] flex-shrink-0" />
              <span>{a.phone}</span>
            </a>
          )}
        </div>
      )}
    </a>
  );
}

export default function BrandingTierPage() {
  const { tier } = useParams();
  const [q, setQ] = useState("");

  const meta = BRANDING_TIERS[tier];

  const filtered = useMemo(() => {
    if (!meta) return [];
    if (!q.trim()) return meta.entries;
    const needle = q.trim().toLowerCase();
    return meta.entries.filter(
      (e) =>
        e.name.toLowerCase().includes(needle) ||
        (e.description || "").toLowerCase().includes(needle) ||
        (e.address || "").toLowerCase().includes(needle),
    );
  }, [meta, q]);

  if (!meta) return <Navigate to="/branding" replace />;

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid={`branding-tier-${tier}`}>
      <div className="max-w-7xl mx-auto px-6">
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <Link to="/branding" className="hover:text-white transition-colors">Branding</Link>
          <span>/</span>
          <span className="text-[#B8D0EE]">{meta.label}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="overline">{meta.overline}</div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98] mt-3">
              {meta.label}
            </h1>
            <div className="overline mt-4">
              {meta.entries.length} {meta.entries.length === 1 ? "entry" : "entries"} indexed
            </div>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed">{meta.intro}</p>
          </div>
        </div>

        <div className="glass-card p-5 mb-8 relative">
          <Search className="w-4 h-4 absolute left-9 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className="input-dark pl-10"
            placeholder={`Search within ${meta.label}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="brand-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No entries match your search.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="brand-grid">
            {filtered.map((a) => (
              <BrandCard key={a.name} a={a} />
            ))}
          </div>
        )}

        {/* Other tiers */}
        <section className="mt-20" data-testid="other-branding-tiers">
          <div className="overline mb-3">Other branding tiers</div>
          <h2 className="font-display text-2xl lg:text-3xl mb-6">Continue exploring</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(BRANDING_TIERS)
              .filter(([k]) => k !== tier)
              .map(([k, t]) => (
                <Link
                  key={k}
                  to={`/branding/${k}`}
                  className="glass-card p-5 group"
                  data-testid={`other-branding-${k}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#7DA3D9]">
                    {t.overline}
                  </div>
                  <div className="font-display text-lg mt-2 group-hover:text-[#B8D0EE] transition-colors">
                    {t.label}
                  </div>
                  <div className="text-xs text-slate-400 mt-2">{t.entries.length} entries →</div>
                </Link>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
}
